import React, { useMemo } from 'react';
import * as THREE from 'three';
import { createBasicWireframeMaterial, createHexagonMaterial } from '../materials/wireframeMaterials';
import accountStore from '../../../../../components/account/AccountStore';

function DNAStrand({ 
  height = 125,
  radius = 1.8,
  segments = 300,
  nucleotidesPerTurn = 12,
  color = '#00ff00'
}) {
  const dnaGeometry = useMemo(() => {
    const points = [];
    const points2 = []; // Second helix
    const connections = []; // Cross connections
    const connectionPoints = []; // Points for spheres
    
    // Calculate points for both helices
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * height;
      const angle = (i / nucleotidesPerTurn) * Math.PI * 2;
      
      // First helix
      const x1 = Math.cos(angle) * radius;
      const z1 = Math.sin(angle) * radius;
      
      // Second helix (180 degrees offset)
      const x2 = Math.cos(angle + Math.PI) * radius;
      const z2 = Math.sin(angle + Math.PI) * radius;
      
      if (i < segments) {
        const nextT = ((i + 1) / segments) * height;
        const nextAngle = ((i + 1) / nucleotidesPerTurn) * Math.PI * 2;
        
        // First helix segment
        const nextX1 = Math.cos(nextAngle) * radius;
        const nextZ1 = Math.sin(nextAngle) * radius;
        points.push(x1, t, z1, nextX1, nextT, nextZ1);
        
        // Second helix segment
        const nextX2 = Math.cos(nextAngle + Math.PI) * radius;
        const nextZ2 = Math.sin(nextAngle + Math.PI) * radius;
        points2.push(x2, t, z2, nextX2, nextT, nextZ2);
      }
      
      // Add cross connections and connection points every half turn
      if (i % (nucleotidesPerTurn / 2) === 0) {
        // Store connection points for spheres
        connectionPoints.push(
          { x: x1, y: t, z: z1 },
          { x: x2, y: t, z: z2 }
        );

        // Main connection
        connections.push(x1, t, z1, x2, t, z2);
        
        // Additional connection details (small perpendicular lines)
        const perpLength = radius * 0.3;
        
        // Calculate perpendicular vector
        const dx = x2 - x1;
        const dz = z2 - z1;
        const perpX = -dz / Math.sqrt(dx * dx + dz * dz) * perpLength;
        const perpZ = dx / Math.sqrt(dx * dx + dz * dz) * perpLength;
        
        // Add perpendicular lines at 1/3 and 2/3 of the connection
        const third1X = x1 + (x2 - x1) / 3;
        const third1Z = z1 + (z2 - z1) / 3;
        const third2X = x1 + 2 * (x2 - x1) / 3;
        const third2Z = z1 + 2 * (z2 - z1) / 3;
        
        connections.push(
          third1X - perpX, t, third1Z - perpZ,
          third1X + perpX, t, third1Z + perpZ,
          third2X - perpX, t, third2Z - perpZ,
          third2X + perpX, t, third2Z + perpZ
        );
      }
    }
    
    // Combine all points
    const allPoints = [...points, ...points2, ...connections];
    
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(allPoints, 3));
    return { geometry, connectionPoints };
  }, [height, radius, segments, nucleotidesPerTurn]);

  const material = useMemo(() => {
    const allowedHexCount = accountStore.getAllowedHexCount();
    const totalHexes = 50;
    const transitionHeight = (allowedHexCount / totalHexes) * height;

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        color1: { value: new THREE.Color('#00ff00') },
        color2: { value: new THREE.Color('#ff0000') },
        height: { value: height },
        transitionHeight: { value: transitionHeight },
        opacity: { value: 0.7 }
      },
      vertexShader: `
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 color1;
        uniform vec3 color2;
        uniform float height;
        uniform float transitionHeight;
        uniform float opacity;
        varying vec3 vPosition;
        void main() {
          vec3 finalColor = vPosition.y <= transitionHeight ? color1 : color2;
          gl_FragColor = vec4(finalColor, opacity);
        }
      `,
      transparent: true,
      depthWrite: false,
      renderOrder: 1
    });
    return mat;
  }, [height]);

  const createSphereMaterial = (point) => {
    const allowedHexCount = accountStore.getAllowedHexCount();
    const totalHexes = 50;
    const transitionHeight = (allowedHexCount / totalHexes) * height;
    const sphereColor = point.y <= transitionHeight ? '#00ff00' : '#ff0000';
    
    const mat = createHexagonMaterial(sphereColor, 0.4);
    mat.transparent = true;
    mat.depthWrite = false;
    mat.renderOrder = 1;
    return mat;
  };

  return (
    <group renderOrder={1}>
      <lineSegments name="dna-strands">
        <primitive object={dnaGeometry.geometry} />
        <primitive object={material} />
      </lineSegments>
      
      {/* Connection point spheres */}
      {dnaGeometry.connectionPoints.map((point, index) => (
        <mesh
          key={`sphere-${index}`}
          position={[point.x, point.y, point.z]}
          name={`dna-sphere-${index}`}
        >
          <sphereGeometry args={[0.15, 8, 8]} />
          <primitive object={createSphereMaterial(point)} />
        </mesh>
      ))}
    </group>
  );
}

export default DNAStrand;